/**
 * Previsualización del grabado: el cliente coloca su texto sobre la foto.
 *
 * Todo se dibuja en un lienzo, no en SVG, por dos motivos: el texto curvado se
 * resuelve girando cada letra alrededor de un centro —algo que el lienzo hace
 * de forma natural— y, sobre todo, porque la misma función que pinta la
 * pantalla genera la imagen que se adjunta al pedido. Un solo camino de
 * dibujo, así lo que ve el cliente y lo que recibe el taller no pueden
 * desincronizarse.
 *
 * Hay una «escena» por zona grabable:
 *
 *   virola         aro visto desde arriba; hasta dos frases, cada una siguiendo
 *                  la curva del aro, sin saltos de línea
 *   cuerpo         rectángulo; el texto va recto, admite varias líneas y puede
 *                  acompañarse del logo que haya subido el cliente
 *   virola,cuerpo  las dos escenas a la vez: las dos frases en el aro y un
 *                  tercer texto para el costado
 *   bombilla       la caña; el texto va en vertical, en una sola línea
 *   termo          el cuerpo del termo; igual que el cuerpo del mate
 *
 * Las figuras de texto no son piezas aparte: se insertan en el propio texto,
 * así el cliente coloca un solo bloque y no tiene que cuadrar varios
 * elementos. Los íconos dibujados (mate, Sol de Mayo, estrellas…), en cambio,
 * sí son piezas: cada uno se mueve y se agranda por su cuenta. En el aro
 * giran siguiendo la curva, como las letras.
 *
 * Cada frase del aro guarda hacia dónde miran sus letras: «out» las aleja del
 * centro y «in» las gira hacia él, que es lo que de verdad distingue un
 * grabado mirando al exterior del mate de uno mirando a su interior. No se
 * deduce de la posición: si se dedujera, el mismo botón haría cosas distintas
 * arriba y abajo del aro.
 *
 * Cuando hay más de una pieza en una escena, el cliente elige cuál está
 * ajustando: con dos frases superpuestas, adivinar cuál se arrastra por
 * cercanía era incómodo y poco predecible.
 *
 * El área grabable la define el taller en porcentaje de la foto, así no
 * depende del tamaño en pantalla ni de la resolución de la imagen:
 *
 *   virola  centroX,centroY,radioInterior,radioExterior
 *   cuerpo  x,y,ancho,alto
 */

const EXPORT_WIDTH = 1400;
const LOGO_SLOT = 9;
/** El otro costado: su texto (data-text4) y su logo (data-logo2). */
const SECOND_TEXT_SLOT = 4;
const LOGO2_SLOT = 10;
/** Prefijo de las piezas de ícono: «icon:sol-de-mayo~2» (cada copia, aparte). */
const ICON_PREFIX = 'icon:';
/** Ancho de un ícono a tamaño 100, en proporción al ancho de la foto. */
const ICON_SCALE = 0.25;
/** Giro de los íconos: hasta media vuelta a cada lado; cerca de 0 se endereza. */
const ROTATE_LIMIT = 180;
const ROTATE_SNAP = 4;
const ROTATE_LABEL = 'Giro';

/**
 * La imagen para el taller viaja con el formulario al añadir al carrito, así
 * que su peso es tiempo de espera para el cliente. En JPG pesa una décima
 * parte que en PNG, y todos los navegadores la generan igual. No necesita
 * transparencia: lleva la foto de fondo.
 */
const EXPORT_TYPE = 'image/jpeg';
const EXPORT_QUALITY = 0.9;
const EXPORT_NAME = 'grabado.jpg';

/** Mano que señala, en trazo: la indicación de que el diseño se puede mover. */
const HAND_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false">' +
  '<path d="M10 11V4.5a1.5 1.5 0 0 1 3 0V10"/>' +
  '<path d="M13 9.5a1.5 1.5 0 0 1 3 0V11"/>' +
  '<path d="M16 10.5a1.5 1.5 0 0 1 3 0V15a6 6 0 0 1-6 6h-1.5a6 6 0 0 1-4.6-2.2L4.3 15.6a1.5 1.5 0 0 1 2.3-1.9L10 17"/>' +
  '</svg>';

class MrPreview extends HTMLElement {
  #controller = new AbortController();
  #exportTimer = 0;
  /** @type {Map<HTMLElement, object[]>} */
  #items = new Map();
  #dragging = null;
  /** @type {HTMLImageElement | null} */
  #logo = null;
  /** Logo del otro costado. @type {HTMLImageElement | null} */
  #logo2 = null;
  /** Zona en la que va cada ícono elegido: id → «virola» o «cuerpo». */
  #iconZones = new Map();
  /** Dibujos de los íconos ya listos para el lienzo, por id y color. */
  #iconImages = new Map();
  /** Última zona que tocó el cliente: ahí entran los íconos nuevos. */
  #currentZone = '';

  static observedAttributes = [
    'data-text1',
    'data-text2',
    'data-text3',
    'data-text4',
    'data-font',
    'data-zones',
    'data-logo',
    'data-logo2',
    'data-icons',
  ];

  connectedCallback() {
    const { signal } = this.#controller;

    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.addEventListener('pointerup', this.#handlePointerUp, { signal });
    this.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    this.addEventListener('input', this.#handleControl, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    window.addEventListener('resize', this.#drawAll, { signal });

    for (const image of this.querySelectorAll('.mr-preview__image')) {
      image.addEventListener('load', this.#drawAll, { signal });
    }

    this.#addHints();
    this.#addRotateControls();
    this.#sync();
  }

  /**
   * Deslizador «Giro» debajo de los controles de cada escena. Solo se ve con
   * un ícono elegido: el texto y el logo van derechos (o siguen el aro).
   */
  #addRotateControls() {
    for (const controls of this.querySelectorAll('.mr-preview__controls')) {
      if (controls.parentElement?.querySelector('[data-mr-rotate]')) continue;

      const label = document.createElement('label');
      label.className = 'mr-preview__control';
      label.dataset.mrRotateWrap = '';
      label.style.display = 'none';

      const name = document.createElement('span');
      name.textContent = ROTATE_LABEL;

      const input = document.createElement('input');
      input.type = 'range';
      input.min = String(-ROTATE_LIMIT);
      input.max = String(ROTATE_LIMIT);
      input.step = '1';
      input.value = '0';
      input.dataset.mrRotate = '';

      label.append(name, input);
      controls.after(label);
    }
  }

  /**
   * Manito en la esquina de cada foto: avisa que el diseño se puede mover.
   * Es decorativa (el lector de pantalla no la anuncia) y no tapa el lienzo:
   * los toques la atraviesan. Se desvanece la primera vez que se toca la foto.
   */
  #addHints() {
    for (const frame of this.querySelectorAll('.mr-preview__frame')) {
      if (frame.querySelector('.mr-preview__hint')) continue;

      const hint = document.createElement('span');
      hint.className = 'mr-preview__hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.innerHTML = HAND_ICON;
      frame.append(hint);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  attributeChangedCallback(name) {
    if (name === 'data-logo' || name === 'data-logo2') {
      this.#loadLogo(name === 'data-logo2');
      return;
    }

    if (name === 'data-zones' || name === 'data-icons') this.#sync();
    else this.#drawAll();
  }

  /**
   * Zonas que tienen al menos un ícono. El bloque de grabado lo consulta para
   * saber si, grabando virola y costado, hay algo en cada una.
   *
   * @returns {Set<string>}
   */
  get zonesWithIcons() {
    return new Set(this.#iconZones.values());
  }

  /** Escena donde entra el próximo ícono (la elige el bloque de grabado). */
  set targetZone(zone) {
    this.#currentZone = zone || '';
  }

  /**
   * Escena en la que está un ícono agregado.
   *
   * @param {string} uid
   */
  iconZone(uid) {
    return this.#iconZones.get(uid) ?? '';
  }

  /** Avisa al bloque de grabado qué escena tocó el cliente. */
  #announceZone() {
    this.dispatchEvent(new CustomEvent('mr-preview:zone', { bubbles: true, detail: { zone: this.#currentZone } }));
  }

  get #stages() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-mr-stage]')]);
  }

  /** Íconos elegidos, en el orden en que se tocaron. */
  get #iconIds() {
    return (this.dataset.icons || '').split(',').filter(Boolean);
  }

  /**
   * Decide en qué zona va cada ícono. Uno nuevo entra en la última zona que
   * tocó el cliente; si su zona deja de estar (cambió de virola a costado),
   * pasa a la primera que haya.
   *
   * @param {string[]} zones
   */
  #assignIcons(zones) {
    const ids = this.#iconIds;

    for (const id of [...this.#iconZones.keys()]) {
      if (!ids.includes(id)) this.#iconZones.delete(id);
    }

    for (const id of ids) {
      const zone = this.#iconZones.get(id);
      if (zone && zones.includes(zone)) continue;
      this.#iconZones.set(id, zones.includes(this.#currentZone) ? this.#currentZone : zones[0]);
    }
  }

  /** Reparte las piezas entre las escenas y decide cuáles se ven. */
  #sync() {
    const zones = (this.dataset.zones || 'virola').split(',');
    // Virola y costado: el costado lleva el tercer texto. El otro costado
    // («cuerpo2», «termo2») no cuenta: tiene su propio texto.
    const both = zones.includes('virola') && zones.filter((zone) => !zone.endsWith('2')).length > 1;

    this.#assignIcons(zones);

    for (const stage of this.#stages) {
      const zone = stage.dataset.zone ?? '';
      const active = zones.includes(zone);

      stage.hidden = !active;
      if (!active) {
        this.#items.delete(stage);
        continue;
      }

      // En la virola caben dos frases; en el cuerpo, un texto y el logo
      /** @type {(number | string)[]} */
      let slots;
      if (zone === 'virola') {
        slots = [1, 2];
      } else if (isSecond(zone)) {
        slots = [SECOND_TEXT_SLOT];
        if (this.#logo2) slots.push(LOGO2_SLOT);
      } else {
        slots = [both ? 3 : 1];
        if (this.#logo) slots.push(LOGO_SLOT);
      }
      for (const [id, iconZone] of this.#iconZones) {
        if (iconZone === zone) slots.push(`${ICON_PREFIX}${id}`);
      }

      this.#items.set(
        stage,
        slots.map((slot) => this.#createItem(stage, slot))
      );
    }

    this.#drawAll();
  }

  /** Estado inicial de una pieza: centrada en su área grabable. */
  #createItem(stage, slot) {
    const previous = (this.#items.get(stage) ?? []).find((item) => item.slot === slot);
    if (previous) return previous;

    const area = areaOf(stage);
    const isLogo = slot === LOGO_SLOT || slot === LOGO2_SLOT;
    const icon = typeof slot === 'string' && slot.startsWith(ICON_PREFIX) ? slot.slice(ICON_PREFIX.length) : '';

    // Bombilla: todo sobre la caña, centrado. El texto va en vertical; los
    // íconos y el logo, derechos y escalonados a lo largo de la caña.
    if (stage.dataset.zone === 'bombilla') {
      const centerX = area.x + area.width / 2;

      if (icon) {
        const count = Math.max(0, this.#iconIds.indexOf(icon));
        return {
          slot,
          type: 'icon',
          icon,
          x: centerX,
          // A tamaño 40 el ícono ocupa el ancho de la caña
          y: area.y + area.height * (0.06 + 0.11 * (count % 9)),
          size: 40,
        };
      }

      return {
        slot,
        type: isLogo ? 'image' : 'text',
        x: centerX,
        y: area.y + area.height * (isLogo ? 0.88 : 0.5),
        size: isLogo ? 10 : 30,
        facing: 'up',
      };
    }

    if (icon) {
      // Cada ícono se corre según su orden, para no caer encima del anterior
      const count = Math.max(0, this.#iconIds.indexOf(icon));

      if (stage.dataset.zone === 'virola') {
        return {
          slot,
          type: 'icon',
          icon,
          angle: -Math.PI / 2 + (count + 1) * 0.55,
          radius: (area.inner + area.outer) / 2,
          size: 40,
          facing: 'out',
        };
      }

      return {
        slot,
        type: 'icon',
        icon,
        // Tres por fila; con más, la siguiente fila, para que no se tapen
        x: clamp(area.x + area.width * (0.25 + 0.25 * (count % 3)), area.x, area.x + area.width),
        y: area.y + area.height * (0.7 - 0.3 * (Math.floor(count / 3) % 3)),
        size: isTermo(stage.dataset.zone) ? 30 : 40,
      };
    }

    if (stage.dataset.zone === 'virola') {
      return {
        slot,
        type: 'text',
        angle: slot === 1 ? -Math.PI / 2 : Math.PI / 2,
        radius: (area.inner + area.outer) / 2,
        size: 40,
        // Arriba mirando afuera y abajo mirando adentro: así las dos frases
        // se leen de entrada en su posición inicial.
        facing: slot === 1 ? 'in' : 'out',
      };
    }

    // El cuerpo del termo es angosto: las piezas arrancan más chicas
    const narrow = isTermo(stage.dataset.zone);

    return {
      slot,
      type: isLogo ? 'image' : 'text',
      x: area.x + area.width / 2,
      y: area.y + area.height * (isLogo ? 0.75 : 0.4),
      size: isLogo ? (narrow ? 12 : 25) : narrow ? 20 : 40,
    };
  }

  /** @param {number} slot */
  #textOf(slot) {
    return (this.dataset[`text${slot}`] || '').trim();
  }

  /**
   * El logo llega como URL de objeto: se carga una vez y se redibuja.
   *
   * @param {boolean} [second] - El del otro costado.
   */
  #loadLogo(second = false) {
    const source = second ? this.dataset.logo2 : this.dataset.logo;

    /** @param {HTMLImageElement | null} image */
    const set = (image) => {
      if (second) this.#logo2 = image;
      else this.#logo = image;
      this.#sync();
    };

    if (!source) {
      set(null);
      return;
    }

    const image = new Image();
    image.onload = () => {
      // Si mientras cargaba llegó otro, este ya no cuenta
      if ((second ? this.dataset.logo2 : this.dataset.logo) === source) set(image);
    };
    image.src = source;
  }

  /** Logo que dibuja una pieza de imagen, según su escena. */
  #logoOf(item) {
    return item.slot === LOGO2_SLOT ? this.#logo2 : this.#logo;
  }

  /** Pieza que se está ajustando en esa escena. */
  #activeItem(stage) {
    const items = (this.#items.get(stage) ?? []).filter((item) => this.#isVisible(item));
    return items.find((item) => item.active) ?? items[0] ?? null;
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const stage = target.closest('[data-mr-stage]');
    if (!(stage instanceof HTMLElement)) return;

    this.#currentZone = stage.dataset.zone ?? '';
    this.#announceZone();

    const piece = target.closest('[data-mr-piece]');
    if (piece instanceof HTMLElement) {
      for (const item of this.#items.get(stage) ?? []) item.active = String(item.slot) === piece.dataset.mrPiece;
      this.#draw(stage);
      return;
    }

    if (target.closest('[data-mr-flip]')) {
      const item = this.#activeItem(stage);
      if (item && stage.dataset.zone === 'bombilla') {
        // Tres modos en rueda: de abajo arriba, de arriba abajo y letra a letra
        item.facing = item.facing === 'up' ? 'down' : item.facing === 'down' ? 'stack' : 'up';
      } else if (item) {
        // Hacia dónde miran las letras: al exterior del mate o a su interior
        item.facing = item.facing === 'in' ? 'out' : 'in';
      }
      this.#draw(stage);
    }
  };

  #handleControl = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-mr-rotate]')) {
      const stage = target.closest('[data-mr-stage]');
      if (!(stage instanceof HTMLElement)) return;

      // El giro es del ícono elegido; cerca de 0 vuelve a quedar derecho
      const item = this.#activeItem(stage);
      if (item?.type === 'icon') {
        let degrees = Number(target.value) || 0;
        if (Math.abs(degrees) <= ROTATE_SNAP) {
          degrees = 0;
          target.value = '0';
        }
        item.rotation = degrees;
      }

      this.#draw(stage);
      this.#scheduleExport();
      return;
    }

    if (!target.matches('[data-mr-size]')) return;

    const stage = target.closest('[data-mr-stage]');
    if (!(stage instanceof HTMLElement)) return;

    // El deslizador cambia el tamaño de la última pieza que se tocó
    const item = this.#activeItem(stage);
    if (item) item.size = Number(target.value);

    // La imagen para el taller también tiene que llevar el tamaño nuevo
    this.#draw(stage);
    this.#scheduleExport();
  };

  #handlePointerDown = (event) => {
    const canvas = event.target;
    if (!(canvas instanceof HTMLCanvasElement)) return;

    const stage = canvas.closest('[data-mr-stage]');
    if (!(stage instanceof HTMLElement)) return;

    this.#currentZone = stage.dataset.zone ?? '';
    this.#announceZone();
    stage.dataset.mrTouched = 'true';

    // Se arrastra la pieza elegida en el selector: con dos frases encimadas,
    // mover «la más cercana» era impredecible.
    const item = this.#activeItem(stage);
    if (!item) return;

    // Se anota el punto de partida para mover por diferencia: si se colocara
    // la pieza bajo el dedo, al tocar daría un salto.
    this.#dragging = {
      stage,
      item,
      origin: pointIn(canvas, event),
      start: { angle: item.angle, radius: item.radius, x: item.x, y: item.y },
    };

    canvas.setPointerCapture(event.pointerId);
  };

  #handlePointerMove = (event) => {
    if (this.#dragging && event.target instanceof HTMLCanvasElement) this.#moveTo(event.target, event);
  };

  #handlePointerUp = (event) => {
    // Solo al soltar un arrastre en la foto. Si se redibujara en cualquier
    // «pointerup» (p. ej. al tocar un botón de pieza), los botones se
    // rehacían antes del «click» y la elección se perdía.
    if (!this.#dragging) return;

    if (event.target instanceof HTMLCanvasElement) event.target.releasePointerCapture(event.pointerId);
    this.#dragging = null;
    this.#drawAll();
  };

  #isVisible(item) {
    if (item.type === 'icon') return true;
    return item.type === 'image' ? Boolean(this.#logoOf(item)) : Boolean(this.#textOf(item.slot));
  }

  /** Nombre del ícono, tal como lo muestra su botón. */
  #iconName(id) {
    const base = iconBase(id);
    const button = this.closest('mr-engraving')?.querySelector(`[data-mr-icon="${CSS.escape(base)}"]`);
    return button instanceof HTMLElement ? button.dataset.mrIconName || base : base;
  }

  /**
   * El dibujo del ícono como imagen para el lienzo, en el color del grabado
   * de esa zona. Sale del mismo dibujo del botón: uno solo para la pantalla,
   * la previsualización y el taller.
   *
   * @param {string} uid - El ícono, o una copia suya («bloque~n»)
   * @param {string} ink
   * @returns {HTMLImageElement | HTMLCanvasElement | null} La imagen, o null si aún no cargó.
   */
  #iconImage(uid, ink) {
    // Las copias de un mismo ícono comparten dibujo
    const id = iconBase(uid);
    const key = `${id}|${ink}`;
    const cached = this.#iconImages.get(key);
    if (cached instanceof HTMLCanvasElement) return cached;
    // Una imagen propia solo se dibuja ya teñida, nunca con sus colores
    if (cached?.dataset.mrTint) return null;
    if (cached) return cached.complete && cached.naturalWidth ? cached : null;

    const button = this.closest('mr-engraving')?.querySelector(`[data-mr-icon="${CSS.escape(id)}"]`);

    // Imagen propia: se tiñe del color del grabado conservando su silueta
    const source = button?.querySelector('[data-mr-icon-src]');
    if (source instanceof HTMLElement && source.dataset.mrIconSrc) {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.dataset.mrTint = 'true';
      image.onload = () => {
        this.#iconImages.set(key, tint(image, ink));
        this.#drawAll();
      };
      image.src = source.dataset.mrIconSrc;
      this.#iconImages.set(key, image);
      return null;
    }

    const svg = button?.querySelector('svg');
    if (!(svg instanceof SVGElement)) return null;

    const clone = /** @type {SVGElement} */ (svg.cloneNode(true));
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', '512');
    clone.setAttribute('height', '512');
    const markup = new XMLSerializer().serializeToString(clone).replaceAll('currentColor', ink);

    const image = new Image();
    image.onload = () => this.#drawAll();
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
    this.#iconImages.set(key, image);

    return null;
  }

  /** Mueve la pieza lo mismo que se ha movido el dedo, sin salir del área. */
  #moveTo(canvas, event) {
    if (!this.#dragging) return;

    const { stage, item, origin, start } = this.#dragging;
    const point = pointIn(canvas, event);
    const area = areaOf(stage);

    if (stage.dataset.zone === 'virola') {
      // En el aro el desplazamiento se mide en ángulo y en distancia al centro
      const rect = canvas.getBoundingClientRect();
      const ratio = rect.height / rect.width;
      const from = polar(origin, area, ratio);
      const to = polar(point, area, ratio);

      item.angle = start.angle + angleDelta(from.angle, to.angle);
      item.radius = clamp(start.radius + (to.radius - from.radius), area.inner, area.outer);
    } else {
      item.x = clamp(start.x + (point.x - origin.x), area.x, area.x + area.width);
      item.y = clamp(start.y + (point.y - origin.y), area.y, area.y + area.height);
    }

    this.#draw(stage);
  }

  #drawAll = () => {
    for (const stage of this.#stages) {
      if (!stage.hidden) this.#draw(stage);
    }

    this.#scheduleExport();
  };

  /** @param {HTMLElement} stage */
  #draw(stage) {
    const canvas = stage.querySelector('[data-mr-canvas]');
    const image = stage.querySelector('.mr-preview__image');
    if (!(canvas instanceof HTMLCanvasElement) || !(image instanceof HTMLImageElement) || !image.clientWidth) return;

    const dpr = window.devicePixelRatio || 1;
    const width = image.clientWidth;
    const height = image.clientHeight || width;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    if (this.#dragging?.stage === stage) this.#drawArea(ctx, stage, width, height);
    this.#drawItems(ctx, stage, width, height);
    this.#renderPieces(stage);
  }

  /**
   * Botones para elegir qué pieza se ajusta. Se etiquetan con su propio
   * contenido —el principio de la frase, o el nombre del logo— para que no
   * haga falta explicar cuál es cuál.
   *
   * @param {HTMLElement} stage
   */
  #renderPieces(stage) {
    const list = stage.querySelector('[data-mr-pieces]');
    if (!(list instanceof HTMLElement)) return;

    const items = (this.#items.get(stage) ?? []).filter((item) => this.#isVisible(item));
    const active = this.#activeItem(stage);

    // Con una sola pieza no hay nada que elegir, ni nada que explicar
    list.hidden = items.length < 2;

    const hint = stage.querySelector('[data-mr-pieces-label]');
    if (hint instanceof HTMLElement) hint.hidden = list.hidden || !hint.textContent?.trim();

    // Las copias de un mismo ícono se numeran para distinguirlas
    /** @type {Map<string, number>} */
    const totals = new Map();
    for (const item of items) {
      if (item.type === 'icon') totals.set(iconBase(item.icon), (totals.get(iconBase(item.icon)) ?? 0) + 1);
    }
    /** @type {Map<string, number>} */
    const seen = new Map();

    /** @type {{ slot: string, label: string, selected: boolean }[]} */
    const pieces = items.map((item) => {
      let label;
      if (item.type === 'icon') {
        const base = iconBase(item.icon);
        const number = (seen.get(base) ?? 0) + 1;
        seen.set(base, number);
        label = (totals.get(base) ?? 0) > 1 ? `${this.#iconName(item.icon)} ${number}` : this.#iconName(item.icon);
      } else {
        label = item.type === 'image' ? this.dataset.logoLabel || 'Logo' : truncate(this.#textOf(item.slot).replace(/\s*\n\s*/g, ' '));
      }
      return { slot: String(item.slot), label, selected: item === active };
    });

    // Si las piezas son las mismas, se reutilizan los botones y solo se marca
    // la elegida: rehacerlos en cada dibujo (al mover, al cargar una imagen,
    // al girar el celular) podía borrar el botón justo cuando se tocaba.
    const signature = pieces.map((piece) => `${piece.slot}\u0000${piece.label}`).join('\u0001');
    if (list.dataset.mrSignature !== signature || list.children.length !== pieces.length) {
      list.replaceChildren(
        ...pieces.map((piece) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'mr-preview__piece';
          button.dataset.mrPiece = piece.slot;
          button.textContent = piece.label;
          return button;
        })
      );
      list.dataset.mrSignature = signature;
    }

    pieces.forEach((piece, index) => {
      list.children[index]?.toggleAttribute('data-selected', piece.selected);
    });

    const size = stage.querySelector('[data-mr-size]');
    if (size instanceof HTMLInputElement && active) size.value = String(active.size);

    // El giro, solo para íconos, con el valor del ícono elegido
    const rotateWrap = stage.querySelector('[data-mr-rotate-wrap]');
    if (rotateWrap instanceof HTMLElement) {
      const isIcon = active?.type === 'icon';
      rotateWrap.style.display = isIcon ? '' : 'none';

      const rotate = rotateWrap.querySelector('[data-mr-rotate]');
      if (isIcon && rotate instanceof HTMLInputElement) rotate.value = String(active.rotation ?? 0);
    }

    const flip = stage.querySelector('[data-mr-flip]');
    if (flip instanceof HTMLElement && stage.dataset.zone === 'bombilla') {
      // En la bombilla solo el texto tiene sentido: íconos y logo van derechos
      flip.hidden = !active || active.type !== 'text';

      if (!flip.hidden && active) {
        const labels = (this.dataset.directionLabels || '').split('|');
        flip.textContent =
          active.facing === 'down'
            ? labels[1] || 'Se lee de arriba abajo'
            : active.facing === 'stack'
              ? labels[2] || 'Letra a letra'
              : labels[0] || 'Se lee de abajo arriba';
        flip.dataset.facing = active.facing;
      }
    } else if (flip instanceof HTMLElement) {
      flip.hidden = stage.dataset.zone !== 'virola' || !active || active.type === 'image';

      if (!flip.hidden && active) {
        const labels = (this.dataset.facingLabels || '').split('|');
        flip.textContent = active.facing === 'in' ? labels[1] || 'Mira hacia dentro' : labels[0] || 'Mira hacia fuera';
        flip.dataset.facing = active.facing;
      }
    }

    // Sobre una pieza circular, una de las dos orientaciones se ve cabeza
    // abajo según en qué punto del aro esté. No es un fallo del dibujo, así
    // que se avisa justo cuando ocurre para que no lo parezca.
    const note = stage.querySelector('[data-mr-facing-note]');
    if (note instanceof HTMLElement) {
      const upright = active && (active.facing === 'out' ? Math.sin(active.angle) > 0 : Math.sin(active.angle) < 0);
      note.hidden = stage.dataset.zone !== 'virola' || !active || active.type !== 'text' || Boolean(upright);
      note.textContent = this.dataset.facingNote ?? '';
    }
  }

  /** El área grabable solo se dibuja mientras se arrastra, como guía. */
  #drawArea(ctx, stage, width, height) {
    const area = areaOf(stage);

    ctx.save();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.setLineDash([6, 5]);
    ctx.lineWidth = 1;

    if (stage.dataset.zone === 'virola') {
      for (const radius of [area.inner, area.outer]) {
        ctx.beginPath();
        ctx.arc((area.cx / 100) * width, (area.cy / 100) * height, (radius / 100) * width, 0, Math.PI * 2);
        ctx.stroke();
      }
    } else {
      ctx.strokeRect(
        (area.x / 100) * width,
        (area.y / 100) * height,
        (area.width / 100) * width,
        (area.height / 100) * height
      );
    }

    ctx.restore();
  }

  /**
   * Dibuja las piezas de una escena. Es la misma función para la pantalla y
   * para la imagen que recibe el taller: solo cambia el tamaño del lienzo.
   */
  #drawItems(ctx, stage, width, height) {
    const area = areaOf(stage);
    const ring = stage.dataset.zone === 'virola';
    const ink = inkOf(stage);

    for (const item of this.#items.get(stage) ?? []) {
      if (!this.#isVisible(item)) continue;

      if (item.type === 'icon') {
        const image = this.#iconImage(item.icon, ink);
        if (!image) continue;

        // El lado mayor ocupa el tamaño elegido; el otro guarda la proporción
        const side = (item.size / 100) * width * ICON_SCALE;
        const imageWidth = image instanceof HTMLCanvasElement ? image.width : image.naturalWidth;
        const imageHeight = image instanceof HTMLCanvasElement ? image.height : image.naturalHeight;
        const scale = side / Math.max(imageWidth, imageHeight, 1);
        const drawWidth = imageWidth * scale;
        const drawHeight = imageHeight * scale;

        ctx.save();
        if (ring) {
          // En el aro el ícono se apoya en la curva: su base mira al centro,
          // o al revés si el cliente lo dio vuelta
          const radius = (item.radius / 100) * width;
          ctx.translate(
            (area.cx / 100) * width + radius * Math.cos(item.angle),
            (area.cy / 100) * height + radius * Math.sin(item.angle)
          );
          ctx.rotate(item.angle + Math.PI / 2 + (item.facing === 'in' ? Math.PI : 0));
        } else {
          ctx.translate((item.x / 100) * width, (item.y / 100) * height);
        }
        // Giro elegido por el cliente, sumado al del aro
        if (item.rotation) ctx.rotate((item.rotation * Math.PI) / 180);
        ctx.drawImage(image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
        ctx.restore();
        continue;
      }

      const logo = item.type === 'image' ? this.#logoOf(item) : null;
      if (logo) {
        const logoWidth = (item.size / 100) * width;
        const logoHeight = (logoWidth * logo.naturalHeight) / logo.naturalWidth;

        ctx.drawImage(
          logo,
          (item.x / 100) * width - logoWidth / 2,
          (item.y / 100) * height - logoHeight / 2,
          logoWidth,
          logoHeight
        );
        continue;
      }

      const text = this.#textOf(item.slot);
      const fontSize = (item.size / 100) * height * 0.18;

      ctx.save();
      ctx.font = `${fontSize}px ${this.dataset.font || 'serif'}`;
      ctx.fillStyle = ink;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (stage.dataset.zone === 'bombilla' && item.facing === 'stack') {
        // Letra a letra: cada letra derecha, una debajo de otra. Si la columna
        // no entra en la caña, la letra se achica hasta que entre.
        const chars = [...text.replace(/\s*\n\s*/g, ' ')];
        const fit = ((area.height / 100) * height) / Math.max(chars.length * 1.05, 1);
        const size = Math.min(fontSize, fit);
        const step = size * 1.05;
        const top = (item.y / 100) * height - ((chars.length - 1) * step) / 2;

        ctx.font = `${size}px ${this.dataset.font || 'serif'}`;
        chars.forEach((char, index) => {
          ctx.fillText(char, (item.x / 100) * width, top + index * step);
        });
      } else if (stage.dataset.zone === 'bombilla') {
        // Bombilla: una sola línea en vertical, a lo largo de la caña. «up» se
        // lee de abajo hacia arriba; «down», de arriba hacia abajo.
        ctx.translate((item.x / 100) * width, (item.y / 100) * height);
        ctx.rotate(item.facing === 'down' ? Math.PI / 2 : -Math.PI / 2);
        ctx.fillText(text.replace(/\s*\n\s*/g, ' '), 0, 0);
      } else if (ring) {
        // El texto sigue el aro; abajo se voltea para poder leerse
        const radius = (item.radius / 100) * width;

        drawArcText(
          ctx,
          text.replace(/\s*\n\s*/g, ' '),
          (area.cx / 100) * width,
          (area.cy / 100) * height,
          radius,
          item.angle,
          item.facing === 'out'
        );
      } else {
        // En el cuerpo el texto va recto y respeta los saltos de línea
        const lines = text.split('\n');
        const step = fontSize * 1.2;
        const top = (item.y / 100) * height - ((lines.length - 1) * step) / 2;

        lines.forEach((line, index) => {
          ctx.fillText(line, (item.x / 100) * width, top + index * step);
        });
      }

      ctx.restore();
    }
  }

  /** La imagen para el taller se regenera al parar, no en cada píxel. */
  #scheduleExport() {
    clearTimeout(this.#exportTimer);
    this.#exportTimer = window.setTimeout(() => this.#exportDesign(), 400);
  }

  /**
   * Compone las escenas visibles —foto y piezas— en un solo lienzo y lo
   * adjunta al formulario como archivo. La foto se recarga con crossOrigin
   * para que el lienzo no quede «manchado»; si aun así el navegador lo impide,
   * se exporta solo lo dibujado antes que quedarse sin nada.
   */
  async #exportDesign() {
    const input = this.querySelector('[data-mr-design]');
    const stages = this.#stages.filter((stage) => !stage.hidden);
    const hasContent =
      [1, 2, 3, SECOND_TEXT_SLOT].some((slot) => this.#textOf(slot)) || this.#logo || this.#logo2 || this.#iconIds.length > 0;
    if (!(input instanceof HTMLInputElement) || !stages.length || !hasContent) return;

    await this.#ensureFont();

    const canvas = document.createElement('canvas');
    const ratios = stages.map((stage) => {
      const image = stage.querySelector('.mr-preview__image');
      return (image?.naturalHeight ?? 1) / (image?.naturalWidth ?? 1) || 1;
    });

    canvas.width = EXPORT_WIDTH;
    canvas.height = Math.round(EXPORT_WIDTH * ratios.reduce((sum, ratio) => sum + ratio, 0));

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // El JPG no tiene transparencia: sin un fondo, lo que no cubra la foto
    // saldría negro y el grabado oscuro no se leería.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let offset = 0;
    for (const [index, stage] of stages.entries()) {
      const height = Math.round(EXPORT_WIDTH * ratios[index]);
      const image = stage.querySelector('.mr-preview__image');

      ctx.save();
      ctx.translate(0, offset);

      try {
        if (image instanceof HTMLImageElement) {
          ctx.drawImage(await loadImage(image.currentSrc || image.src), 0, 0, EXPORT_WIDTH, height);
        }
      } catch {
        /* sin foto de fondo, las piezas solas */
      }

      this.#drawItems(ctx, stage, EXPORT_WIDTH, height);
      ctx.restore();

      offset += height;
    }

    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        const transfer = new DataTransfer();
        transfer.items.add(new File([blob], EXPORT_NAME, { type: EXPORT_TYPE }));
        input.files = transfer.files;
      },
      EXPORT_TYPE,
      EXPORT_QUALITY
    );
  }

  /** Espera a la tipografía elegida: sin esto el lienzo dibujaría con otra. */
  async #ensureFont() {
    try {
      await document.fonts.load(`48px ${this.dataset.font || 'serif'}`);
    } catch {
      /* si la fuente no se puede precargar, se dibuja con la de respaldo */
    }
  }
}

/** ¿Es la escena del otro costado («cuerpo2», «termo2»)? @param {string | undefined} zone */
function isSecond(zone) {
  return Boolean(zone?.endsWith('2'));
}

/** El termo y su otro costado: cuerpo angosto. @param {string | undefined} zone */
function isTermo(zone) {
  return zone === 'termo' || zone === 'termo2';
}

/** Área grabable de una escena, en porcentaje de la foto. */
function areaOf(stage) {
  const [a = 50, b = 50, c = 20, d = 30] = (stage.dataset.geometry || '').split(',').map((value) => Number(value.trim()));

  return stage.dataset.zone === 'virola'
    ? { cx: a, cy: b, inner: c, outer: d }
    : { x: a, y: b, width: c, height: d };
}

/** Color del grabado de esa zona; sale de los tokens, no de un valor fijo. */
function inkOf(stage) {
  return getComputedStyle(stage).getPropertyValue('--mr-preview-ink').trim() || '#000000';
}

/** Punto en coordenadas polares respecto al centro del aro. */
function polar(point, area, ratio) {
  const dx = point.x - area.cx;
  const dy = (point.y - area.cy) * ratio;

  return { angle: Math.atan2(dy, dx), radius: Math.hypot(dx, dy) };
}

/** Diferencia de ángulo más corta, para que no pegue una vuelta entera. */
function angleDelta(from, to) {
  let delta = to - from;

  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;

  return delta;
}

function pointIn(canvas, event) {
  const rect = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) / rect.width) * 100,
    y: ((event.clientY - rect.top) / rect.height) * 100,
  };
}

/**
 * Texto siguiendo un arco: cada letra se gira su propio ancho alrededor del
 * centro, de modo que el renglón se curva con el aro.
 *
 * `flip` cambia hacia dónde miran las letras: sin él los remates se alejan del
 * centro, con él lo miran. En ese caso hay que hacer dos cosas a la vez, y
 * hacer solo una es lo que rompía la palabra:
 *
 *   1. girar cada letra 180°, para que mire al centro
 *   2. recorrer el arco en sentido contrario, para que el orden de lectura se
 *      conserve al mirar la pieza desde el otro lado
 *
 * Girar el marco entero, en cambio, además de invertir el orden llevaba el
 * texto al lado opuesto del aro.
 */
function drawArcText(ctx, text, cx, cy, radius, centerAngle, flip) {
  const chars = [...text];
  const widths = chars.map((char) => ctx.measureText(char).width);
  const total = widths.reduce((sum, width) => sum + width, 0);
  const totalAngle = total / radius;
  const direction = flip ? -1 : 1;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(centerAngle + Math.PI / 2);
  ctx.rotate((-direction * totalAngle) / 2);

  for (let i = 0; i < chars.length; i++) {
    const step = (direction * widths[i]) / radius;

    ctx.rotate(step / 2);
    ctx.save();
    ctx.translate(0, -radius);
    if (flip) ctx.rotate(Math.PI);
    ctx.fillText(chars[i], 0, 0);
    ctx.restore();
    ctx.rotate(step / 2);
  }

  ctx.restore();
}

/**
 * Copia de la imagen pintada de un solo color: conserva la silueta (lo que no
 * es transparente) y le pone el color del grabado.
 *
 * @param {HTMLImageElement} image
 * @param {string} ink
 */
function tint(image, ink) {
  const scale = 512 / Math.max(image.naturalWidth, image.naturalHeight, 1);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = ink;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  return canvas;
}

/** @param {string} src */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

/**
 * El ícono de una copia: «bloque~3» → «bloque».
 *
 * @param {string} uid
 */
function iconBase(uid) {
  return uid.split('~')[0];
}

/** @param {string} text */
function truncate(text) {
  return text.length > 14 ? `${text.slice(0, 13)}…` : text;
}

/** @param {number} value @param {number} min @param {number} max */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

if (!customElements.get('mr-preview')) {
  customElements.define('mr-preview', MrPreview);
}
